import React from "react";
import {
  Box,
  Typography,
  Grid,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  HelpOutline as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  TouchAppOutlined as PickStepIcon,
  ConfirmationNumberOutlined as TicketStepIcon,
  VerifiedUserOutlined as FairStepIcon,
  MonetizationOnOutlined as WinStepIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";
import { LOTTERY_NAMES } from "../../../../../static/Constants";

const STEPS = [
  {
    step: "01",
    icon: PickStepIcon,
    title: `Select an Open ${LOTTERY_NAMES.MEGABET} Round`,
    desc: "Browse through live multi-random games created by verified creators with customized matrix sizes and prize pools.",
  },
  {
    step: "02",
    icon: TicketStepIcon,
    title: "Pick Your Number Combination",
    desc: "Select the required quantity of unique numbers from the game board matrix (e.g., 4 numbers from 1 to 20).",
  },
  {
    step: "03",
    icon: FairStepIcon,
    title: "Chainlink VRF Provable Draw",
    desc: "When the round concludes, Chainlink VRF generates cryptographically verifiable on-chain random winning numbers.",
  },
  {
    step: "04",
    icon: WinStepIcon,
    title: "Jackpot Payout or Rollover",
    desc: "Match the winning numbers to receive the jackpot directly to your wallet! If there is no winner, the pot rolls over to multiply the prize.",
  },
];

const FAQS = [
  {
    q: `How does the Multi-Random (${LOTTERY_NAMES.MEGABET}) format work?`,
    a: `In ${LOTTERY_NAMES.MEGABET}, each lottery defines a capacity (total number pool) and pick count (numbers you must select). For instance, in a 4-of-20 game, you pick 4 numbers between 1 and 20. Tickets matching the winning drawn combination win the prize pool.`,
  },
  {
    q: "What is an Accumulator Rollover Jackpot?",
    a: "If a round finishes with no ticket hitting all winning numbers, the accumulated prize pot rolls over into the subsequent round. This creates massive snowballing prize pools for future participants.",
  },
  {
    q: `How is provable fairness guaranteed in ${LOTTERY_NAMES.MEGABET}?`,
    a: `Every ${LOTTERY_NAMES.MEGABET} draw integrates Chainlink Verifiable Random Function (VRF). This generates a cryptographic proof directly on the blockchain verifying that winning numbers were selected completely randomly without any possibility of manipulation by node operators, players, or creators.`,
  },
  {
    q: "When and how do winners receive payouts?",
    a: "Smart contracts automatically settle winners upon resolution of the random draw. Payouts are transferred immediately and trustlessly to the winner's connected Web3 wallet address.",
  },
];

const MegaHowToPlay = () => {
  return (
    <Box sx={{ mt: 6, mb: 4 }}>
      {/* 4 Steps Overview */}
      <Box
        sx={{
          bgcolor: colors.dashboard.cardBg,
          borderRadius: borderRadiusPx.xl,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          p: { xs: 3, md: 4 },
          mb: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: borderRadiusPx.md,
              bgcolor: colors.emerald[50],
              color: colors.emerald[600],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <HelpIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: colors.slate[900], fontSize: "1.1rem" }}
            >
              {`How Autobet ${LOTTERY_NAMES.MEGABET} Works`}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.slate[500] }}>
              A 4-step decentralized multi-number lottery game
            </Typography>
          </Box>
        </Stack>

        <Grid container spacing={3}>
          {STEPS.map((item, idx) => {
            const StepIcon = item.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Box
                  sx={{
                    p: 2.5,
                    bgcolor: colors.slate[50],
                    borderRadius: borderRadiusPx.lg,
                    border: `1px solid ${colors.slate[200]}`,
                    height: "100%",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: borderRadiusPx.md,
                        bgcolor: colors.emerald[100],
                        color: colors.emerald[700],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <StepIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        color: colors.emerald[600],
                        fontSize: "0.85rem",
                      }}
                    >
                      {item.step}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: colors.slate[900], mb: 0.5 }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: colors.slate[600], fontSize: "0.82rem", lineHeight: 1.5 }}
                  >
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Frequently Asked Questions */}
      {/* <Box
        sx={{
          bgcolor: colors.dashboard.cardBg,
          borderRadius: borderRadiusPx.xl,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          p: { xs: 3, md: 4 },
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: colors.slate[900], mb: 2, fontSize: "1.05rem" }}
        >
          Frequently Asked Questions
        </Typography>

        <Stack spacing={1.5}>
          {FAQS.map((faq, index) => (
            <Accordion
              key={index}
              disableGutters
              elevation={0}
              sx={{
                border: `1px solid ${colors.slate[200]}`,
                borderRadius: `${borderRadiusPx.md} !important`,
                "&:before": { display: "none" },
                overflow: "hidden",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: colors.slate[500] }} />}
                sx={{
                  bgcolor: colors.slate[50],
                  px: 2,
                  py: 0.5,
                  "& .MuiAccordionSummary-content": { my: 1 },
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: colors.slate[800] }}
                >
                  {faq.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, py: 1.5, bgcolor: colors.dashboard.cardBg }}>
                <Typography
                  variant="body2"
                  sx={{ color: colors.slate[600], lineHeight: 1.6 }}
                >
                  {faq.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </Box> */}
    </Box>
  );
};

export default MegaHowToPlay;
